import { AriaDescriber, FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { SharedResizeObserver } from '@angular/cdk/observers/private';
import {
    ConnectedPosition,
    FlexibleConnectedPositionStrategy,
    Overlay,
    OverlayConfig,
    OverlayRef,
    ScrollDispatcher,
    ScrollStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    DestroyRef,
    Directive,
    ElementRef,
    HOST_TAG_NAME,
    InjectionToken,
    Injector,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewContainerRef,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subscription, take } from 'rxjs';

import { SharedIntersectionObserver } from './tooltip.utils';

export const DS_TOOLTIP_POSITION_ARRAY = ['left', 'top', 'right', 'bottom'] as const;
export type DsTooltipPosition = (typeof DS_TOOLTIP_POSITION_ARRAY)[number];
export const DS_ARROW_POSITION_ARRAY = ['start', 'middle', 'end'] as const;
export type DsArrowPosition = (typeof DS_ARROW_POSITION_ARRAY)[number];

export interface OverlayRefWithData {
    ref: OverlayRef;
    data: {
        position: DsTooltipPosition;
        arrowPosition: DsArrowPosition;
    };
}

export const OVERLAY_REF_WITH_DATA_TOKEN = new InjectionToken<OverlayRefWithData>('OverlayRef with data');

@Directive({
    selector: '[dsTooltipTriggerFor]',
    standalone: true,
    host: {
        '(click)': 'toggleTooltip($event)',
        '[attr.aria-haspopup]': 'interactive() ? "dialog" : null',
        '[attr.aria-expanded]': '(interactive() && isButton() ? _tooltipToggle() : null)',
    },
})
export class DsTooltipTrigger implements OnInit, OnDestroy, AfterViewInit {
    private readonly _overlay = inject(Overlay);
    private readonly _injector = inject(Injector);
    private readonly _ngZone = inject(NgZone);
    private readonly _vcr = inject(ViewContainerRef);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _sharedIntersectionObserver = inject(SharedIntersectionObserver);
    private readonly _sharedResizeObserver = inject(SharedResizeObserver);
    private readonly _scrollDispatcher = inject(ScrollDispatcher);
    private readonly _ariaDescriber = inject(AriaDescriber);
    private readonly _focusMonitor = inject(FocusMonitor);
    private readonly _tagName = inject(HOST_TAG_NAME, { optional: true });

    public readonly elementRef = inject(ElementRef);

    // internal signals
    private readonly _isVisible = signal(false);
    private readonly _isShown = signal(false);
    private readonly _tooltipToggle = signal(false);
    private readonly _currentTooltipText = signal<HTMLElement | null>(null);
    public readonly isKeyboardFocused = signal(false);

    public readonly closeOutsideViewport = input(false, { transform: booleanAttribute });
    public readonly autoOpen = input(false, { transform: booleanAttribute });
    public readonly fullWidth = input(false, { transform: booleanAttribute });
    public readonly position = input<DsTooltipPosition>('top');
    public readonly arrowPosition = input<DsArrowPosition>('start');
    public readonly autoCloseTime = input(10_000);
    public readonly content = input<TemplateRef<any>>(undefined, { alias: 'dsTooltipTriggerFor' });
    public readonly closeOnScroll = input(false, { transform: booleanAttribute });
    public readonly hasBackdrop = input(false, { transform: booleanAttribute });
    public readonly closeOnInteraction = input(false, { transform: booleanAttribute });
    public readonly tooltipCustomClass = input('');
    public readonly interactive = input(false, { transform: booleanAttribute });

    /* We support full width only for top and bottom positioning
     *  In case there is the requirement in future to show full width also for left and right,
     * then following question have to be answered:
     * - where to place tooltip on full width as it will go over the trigger
     * Then, one can remove this function and override left and right position in media query to follow logic of top or bottom
     * Then, one has to think, on how adjustArrowPositions know, that it is shown full width and where the arrow should be placed
     * */
    private readonly _position = computed(() => {
        if (this.fullWidth() && this.position() !== 'top' && this.position() !== 'bottom') {
            console.warn('Invalid position for full width, defaulting to top');
            return 'top';
        } else {
            return this.position();
        }
    });

    public isButton(): boolean {
        return this._tagName === 'button';
    }

    // eslint-disable-next-line no-restricted-syntax
    @Input()
    set tooltipToggle(value: boolean) {
        this._tooltipToggle.set(value);
        if (!this.overlayRef) {
            return;
        }
        if (value) {
            this.openTooltip();
        } else {
            this.closeTooltip();
        }
    }

    public readonly tooltipToggleChange = output<{ isTooltipOpen: boolean; source: string }>();

    private autoCloseTimeout?: ReturnType<typeof setTimeout>;
    private backdropSubscription?: Subscription;
    private readonly overlaySubscriptions = new Subscription();
    private attachmentsSubscription?: Subscription;
    private detachmentsSubscription?: Subscription;
    private resizeObserver!: ResizeObserver;
    private readonly _lastObservedWidth = signal(0);
    public overlayRef?: OverlayRef;
    public readonly dsTooltipTriggerForContext = input<Record<string, any> | null>(null);

    constructor() {
        /* This effect handles auto open true to open the first time tooltip is shown */
        effect(() => {
            const isVisible = this._isVisible();
            const isAutoOpen = this.autoOpen();
            const isShown = this._isShown();
            const tooltipToggle = this._tooltipToggle();

            const firstTimeAutoOpen = isAutoOpen && !isShown;

            if (isVisible && (tooltipToggle || firstTimeAutoOpen)) {
                // we also have to set the toggle to true if it is not opened but auto open is true
                if (!tooltipToggle) {
                    this._tooltipToggle.set(true);
                }
                // Update signal before opening
                if (!isShown) {
                    untracked(() => {
                        this._isShown.set(true);
                    });
                    this.initializeAutoOpen(isAutoOpen);
                }
            }
        });

        /* This effect closes the tooltip outside viewport if it is true */
        effect(() => {
            const isVisible = this._isVisible();
            const closeOutsideViewport = untracked(() => this.closeOutsideViewport());
            const isOpen = untracked(() => this._tooltipToggle());
            if (closeOutsideViewport && isOpen && !isVisible) {
                this.closeTooltip();
                this._tooltipToggle.set(false);
            }
        });
    }

    ngOnInit(): void {
        this.overlayRef = this.createOverlay();

        this._sharedResizeObserver
            .observe(this.overlayRef.overlayElement, {})
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                this._ngZone.runOutsideAngular(() => {
                    this.adjustArrowPosition();
                });
            });

        this.listenToOverlayState();

        /* Register observer to listen for viewport visibility*/
        this._sharedIntersectionObserver
            .observe(this.elementRef.nativeElement, { threshold: 1 })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((changes) => {
                // Explicitly check if changes has at least one element
                if (changes && changes.length > 0) {
                    const firstChange = changes[0];
                    // Ensure that firstChange is not undefined and that `isIntersecting` is defined
                    if (firstChange) {
                        this._isVisible.set(firstChange.isIntersecting);
                    }
                }
            });

        if (this.closeOnScroll()) {
            const scrollableAncestors = this._scrollDispatcher.getAncestorScrollContainers(this.elementRef);

            const onAncestorScroll = () => {
                this._ngZone.run(() => this.closeTooltip());
            };

            scrollableAncestors.forEach((scrollable) => {
                this._ngZone.runOutsideAngular(() => {
                    scrollable.elementScrolled().pipe(takeUntilDestroyed(this._destroyRef)).subscribe(onAncestorScroll);
                });
            });

            const globalScrollHandler = () => {
                this._ngZone.run(() => this.closeTooltip());
            };

            this._ngZone.runOutsideAngular(() => {
                window.addEventListener('scroll', globalScrollHandler, true);
            });
            // Clean up global listener
            this._destroyRef.onDestroy(() => {
                window.removeEventListener('scroll', globalScrollHandler, true);
            });
        } else {
            // Reposition tooltip on scroll when closeOnScroll is false
            this._ngZone.runOutsideAngular(() => {
                window.addEventListener('scroll', this.onRepositionScroll, true);
            });

            this._destroyRef.onDestroy(() => {
                window.removeEventListener('scroll', this.onRepositionScroll, true);
            });
        }
        const element = this.elementRef.nativeElement as HTMLElement;

        this._focusMonitor
            .monitor(element, true)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((origin: FocusOrigin | null) => {
                this._ngZone.run(() => {
                    const isFocused = origin === 'keyboard';
                    this.isKeyboardFocused.set(isFocused);
                });
            });
    }

    private readonly onRepositionScroll = () => {
        if (this.overlayRef?.hasAttached()) {
            this._ngZone.run(() => {
                this.overlayRef?.updatePosition();
                this.adjustArrowPosition();
            });
        }
    };

    private initializeAutoOpen(shouldAutoClose: boolean): void {
        this._ngZone.onStable.pipe(take(1)).subscribe(() => {
            if (!this.overlayRef?.hasAttached()) {
                this.openTooltip();
                setTimeout(() => this.overlayRef?.updatePosition(), 0);
            }
        });
        if (shouldAutoClose) {
            this.scheduleAutoClose(this.autoCloseTime() ?? 10_000);
        }
    }

    private isHorizontalPosition(position: string): boolean {
        return position === 'top' || position === 'bottom';
    }

    private isVerticalPosition(position: string): boolean {
        return position === 'left' || position === 'right';
    }

    private getRequiredElements() {
        const overlayElement = this.overlayRef?.overlayElement;
        if (!overlayElement) {
            return;
        }

        const arrowElement = overlayElement.querySelector('.ds-tooltip-arrow');
        if (!arrowElement) {
            return;
        }

        const tooltipContent = overlayElement.querySelector('ds-tooltip-content')?.getBoundingClientRect();
        if (!tooltipContent) {
            return;
        }

        const targetRect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
        const arrowSize = arrowElement.getBoundingClientRect();

        return { arrowElement, tooltipContent, targetRect, arrowSize };
    }

    private adjustArrowPosition(): void {
        const elements = this.getRequiredElements();
        if (!elements) {
            return;
        }

        const { arrowElement, tooltipContent, targetRect, arrowSize } = elements;

        // keeping position in const in order to not call function more times
        const position = this._position();

        if (arrowElement instanceof HTMLElement) {
            if (this.isHorizontalPosition(position)) {
                // Calculate default position
                let leftOffset = targetRect.left + targetRect.width / 2 - tooltipContent.left - arrowSize.width / 2;

                // Dynamic check: Is tooltip wide enough for standard positioning?
                // We need space for arrow width plus 2*ARROW_OFFSET for it to fit properly
                const minimumWidthNeeded = arrowSize.width + 2 * ARROW_OFFSET;
                const isTooltipTooSmall = tooltipContent.width < minimumWidthNeeded;

                if (isTooltipTooSmall) {
                    // For small tooltips, center the arrow
                    const centerPosition = (tooltipContent.width - arrowSize.width) / 2;
                    arrowElement.style.left = `${Math.max(centerPosition, 0)}px`;
                } else {
                    // For normal tooltips, use standard positioning
                    leftOffset = Math.max(leftOffset, ARROW_OFFSET);
                    leftOffset = Math.min(leftOffset, tooltipContent.width - CONTAINER_OFFSET - ARROW_OFFSET);
                    arrowElement.style.left = `${leftOffset}px`;
                }

                arrowElement.style.top = '';
            } else if (this.isVerticalPosition(position)) {
                // Calculate default position
                let topOffset = targetRect.top + targetRect.height / 2 - tooltipContent.top - arrowSize.height / 2;

                // Dynamic check: Is tooltip tall enough for standard positioning?
                const minimumHeightNeeded = arrowSize.height + 2 * ARROW_OFFSET;
                const isTooltipTooSmall = tooltipContent.height < minimumHeightNeeded;

                if (isTooltipTooSmall) {
                    // For small tooltips, center the arrow
                    const centerPosition = (tooltipContent.height - arrowSize.height) / 2;
                    arrowElement.style.top = `${Math.max(centerPosition, 0)}px`;
                } else {
                    // For normal tooltips, use standard positioning - keeping original behavior
                    arrowElement.style.top = `${topOffset}px`;
                }

                arrowElement.style.left = '';
            }
        }
    }

    private listenToOverlayState(): void {
        if (!this.overlayRef) {
            return;
        }

        this.attachmentsSubscription = this.overlayRef.attachments().subscribe(() => {
            this.tooltipToggleChange.emit({ isTooltipOpen: true, source: 'overlayAttachment' });
        });

        this.overlaySubscriptions.add(this.attachmentsSubscription);

        this.detachmentsSubscription = this.overlayRef.detachments().subscribe(() => {
            const tooltipText = this._currentTooltipText();
            if (tooltipText && !this.interactive()) {
                this._ariaDescriber.removeDescription(this.elementRef.nativeElement, tooltipText);
                this._currentTooltipText.set(null);
            }
            this._tooltipToggle.set(false); // Update signal
            this.tooltipToggleChange.emit({ isTooltipOpen: false, source: 'overlayDetachment' });
        });

        this.overlaySubscriptions.add(this.detachmentsSubscription);
    }

    private createOverlay(): OverlayRef {
        const overlayConfig = this.getOverlayConfig();
        const panelClasses: string[] = [];
        if (this.fullWidth()) {
            panelClasses.push(`ds-tooltip-full-width`, `ds-tooltip-${this._position()}`);
        }
        // Add custom class if provided
        if (this.tooltipCustomClass) {
            panelClasses.push(this.tooltipCustomClass());
        }
        return this._overlay.create({ ...overlayConfig, panelClass: panelClasses });
    }

    private getOverlayConfig(): OverlayConfig & { backdropClass?: string } {
        const scrollableAncestors = this._scrollDispatcher.getAncestorScrollContainers(this.elementRef);

        let overlayPosition = getOverlayPosition(this._position(), this.arrowPosition());

        const positionStrategy = this._overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions([overlayPosition])
            .withFlexibleDimensions(false)
            .withViewportMargin(8)
            .withPush(true)
            .withScrollableContainers(scrollableAncestors);

        const scrollStrategy: ScrollStrategy = this.closeOnScroll()
            ? this._overlay.scrollStrategies.close()
            : this._overlay.scrollStrategies.reposition();

        return {
            positionStrategy,
            scrollStrategy,
            hasBackdrop: this.hasBackdrop(),
            ...(this.hasBackdrop() && { backdropClass: 'cdk-overlay-transparent-backdrop' }),
        };
    }

    private scheduleAutoClose(delay: number): void {
        if (delay === 0) {
            return;
        } // Skip auto-close if 0
        this._ngZone.runOutsideAngular(() => {
            this.autoCloseTimeout = setTimeout(() => {
                this._ngZone.run(() => {
                    if (this.overlayRef?.hasAttached()) {
                        this.closeTooltip();
                    }
                });
            }, delay);
        });
    }

    toggleTooltip(event: Event | null = null): void {
        event?.stopPropagation();

        if (this.overlayRef?.hasAttached()) {
            this._tooltipToggle.set(false);
            this.closeTooltip();
        } else {
            this._tooltipToggle.set(true);
            this.openTooltip();
        }
    }

    private openTooltip(): void {
        const content = this.content();
        if (this.overlayRef?.hasAttached() || !content || !this.overlayRef) {
            return;
        } // Prevent opening if already open or content is missing

        const injector = Injector.create({
            providers: [
                {
                    provide: OVERLAY_REF_WITH_DATA_TOKEN,
                    useValue: {
                        ref: this.overlayRef,
                        data: { position: this._position(), arrowPosition: this.arrowPosition() },
                    },
                },
            ],
            parent: this._injector,
        });

        const tooltipPortal = new TemplatePortal(content, this._vcr, this.dsTooltipTriggerForContext(), injector);

        this.overlayRef?.attach(tooltipPortal);
        this.listenForCloseOnInteraction();

        if (this.hasBackdrop()) {
            this.listenForBackdropClick();
        }

        const tooltipElement = this.overlayRef?.overlayElement.querySelector('ds-tooltip-content');
        const tooltipText = tooltipElement?.querySelector('.ds-tooltip-title') as HTMLElement | null;

        if (tooltipText && !this.interactive()) {
            this._ariaDescriber.describe(this.elementRef.nativeElement, tooltipText);
            this._currentTooltipText.set(tooltipText);
        }
    }

    private closeTooltip(): void {
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef?.detach();
        }
        this.removeCloseOnInteractionListener();
        this.backdropSubscription?.unsubscribe();
    }

    listenForCloseOnInteraction(): void {
        if (!this.closeOnInteraction()) {
            return; // Skip if CloseOnInteraction is not enabled
        }

        this._ngZone.runOutsideAngular(() => {
            document.addEventListener('click', this.handleCloseOnInteraction, true);
            document.addEventListener('touchmove', this.handleCloseOnInteraction, true);
        });
    }

    removeCloseOnInteractionListener(): void {
        document.removeEventListener('click', this.handleCloseOnInteraction, true);
        document.removeEventListener('touchmove', this.handleCloseOnInteraction, true);
    }

    private readonly handleCloseOnInteraction = (event: MouseEvent | TouchEvent): void => {
        const clickTarget = event.target as HTMLElement;

        if (event.type === 'touchmove') {
            this._ngZone.run(() => this.closeTooltip());
        }

        if (
            this.elementRef.nativeElement instanceof HTMLElement &&
            !this.elementRef.nativeElement.contains(clickTarget) &&
            !this.overlayRef?.overlayElement?.contains(clickTarget)
        ) {
            this._ngZone.run(() => this.closeTooltip());
        }
    };

    listenForBackdropClick(): void {
        this.backdropSubscription = this.overlayRef?.backdropClick().subscribe(() => {
            this.closeTooltip();
        });
    }

    ngAfterViewInit(): void {
        const positionStrategy = this.overlayRef?.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;

        positionStrategy.positionChanges.subscribe(() => {
            this.adjustArrowPosition();
        });

        // Adjust position initially
        this.adjustArrowPosition();
        this._ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries));
            if (this.overlayRef?.overlayElement) {
                this.resizeObserver.observe(this.overlayRef.overlayElement);
                this._lastObservedWidth.set(this.elementRef.nativeElement.getBoundingClientRect().width);
            }
        });
    }

    ngOnDestroy(): void {
        this.cleanupResources();
        this.removeCloseOnInteractionListener();
        if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    private cleanupResources(): void {
        if (this.autoCloseTimeout) {
            this._ngZone.runOutsideAngular(() => clearTimeout(this.autoCloseTimeout));
        }
        this.overlayRef?.dispose();
        this.backdropSubscription?.unsubscribe();
        this.overlaySubscriptions.unsubscribe();
        this.attachmentsSubscription?.unsubscribe();
        this.detachmentsSubscription?.unsubscribe();
    }

    private onResize(entries: ResizeObserverEntry[]) {
        requestAnimationFrame(() => this._ngZone.run(() => this.handleResize(entries)));
    }

    private handleResize(entries: ResizeObserverEntry[]) {
        if (!this._tooltipToggle()) {
            return;
        }

        for (const entry of entries) {
            const newWidth = entry.contentRect.width;
            if (newWidth !== this._lastObservedWidth()) {
                this._lastObservedWidth.set(newWidth);
                if (this.overlayRef?.hasAttached()) {
                    this.overlayRef?.updatePosition();
                    this.adjustArrowPosition();
                }
            }
        }
    }
}

/*
    Offsets: one has to move the tooltip due to arrow which is out of bounding box by size of arrow
    This is for bottom and top, offsetY, and for left and right: offsetX

    The other offset is to move full container to fit arrow head to right position.

    Instead of offsets defined here one can also use a panel class, as the overlay computation
    is taking into account offsets and client bounding rects, both should be fine to be used.
    Has to be refactored if ARROW_SIZE should be dynamically controlled.
*/
const ARROW_HEIGHT = 11;
const ARROW_WIDTH = 20;
const ARROW_OFFSET = 8;
const CONTAINER_OFFSET = ARROW_WIDTH / 2 + ARROW_OFFSET;
const TOOLTIP_POSITIONS: Record<string, ConnectedPosition> = {
    bottomstart: {
        offsetY: ARROW_HEIGHT,
        offsetX: -CONTAINER_OFFSET,
        originX: 'center',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
    },
    bottomend: {
        offsetY: ARROW_HEIGHT,
        offsetX: CONTAINER_OFFSET,
        originX: 'center',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
    },
    bottommiddle: { offsetY: ARROW_HEIGHT, originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
    topstart: {
        offsetY: -ARROW_HEIGHT,
        offsetX: -CONTAINER_OFFSET,
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
    },
    topend: {
        offsetY: -ARROW_HEIGHT,
        offsetX: CONTAINER_OFFSET,
        originX: 'center',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
    },
    topmiddle: { offsetY: -ARROW_HEIGHT, originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
    leftstart: {
        offsetX: -ARROW_HEIGHT,
        offsetY: -CONTAINER_OFFSET,
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'top',
    },
    leftend: {
        offsetX: -ARROW_HEIGHT,
        offsetY: CONTAINER_OFFSET,
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'bottom',
    },
    leftmiddle: { offsetX: -ARROW_HEIGHT, originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
    rightstart: {
        offsetX: ARROW_HEIGHT,
        offsetY: -CONTAINER_OFFSET,
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'top',
    },
    rightend: {
        offsetX: ARROW_HEIGHT,
        offsetY: CONTAINER_OFFSET,
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'bottom',
    },
    rightmiddle: { offsetX: ARROW_HEIGHT, originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
};

function getOverlayPosition(position: DsTooltipPosition, arrowPosition: DsArrowPosition): ConnectedPosition {
    const key = `${position}${arrowPosition}`; // Cast to valid key type
    return (TOOLTIP_POSITIONS[key] ?? TOOLTIP_POSITIONS['topmiddle']) as ConnectedPosition; // Default fallback
}
